<script lang="ts">
  import { onMount, onDestroy } from "svelte";
  import BaseScene from "@engine/BaseScene.svelte";
  import { MatchSession } from "@gametype/match_learning/MatchSession";
  import { RoundGenerator } from "@gametype/match_learning/RoundGenerator";
  import type { MatchItem, MatchCategory } from "@gametype/match_learning/types";

  export let items: MatchItem[];
  export let categories: MatchCategory[];
  export let onComplete: (result: {
    correctCount: number;
    wrongCount: number;
    finalScore: number;
    bestStreak: number;
    itemsMatched: string[];
  }) => void;
  export let onPause: () => void;

  let currentItem: MatchItem | null = null;
  let currentCats: MatchCategory[] = [];
  let feedback: "" | "correct" | "wrong" = "";
  let session: MatchSession;
  let score = 0;
  let streak = 0;
  let factCard: { name: string; text: string } | null = null;

  const listener = (event: string, data: unknown): void => {
    if (event === "session:item-shown") {
      const { item, categories: cats } = data as { item: MatchItem; categories: MatchCategory[] };
      currentItem = item;
      currentCats = cats;
      feedback = "";
      factCard = null;
    } else if (event === "session:answered") {
      const d = data as { answer: { correct: boolean }; item: MatchItem; state: { score: number; streak: number } };
      feedback = d.answer.correct ? "correct" : "wrong";
      score = d.state.score;
      streak = d.state.streak;
      if (d.answer.correct) {
        factCard = { name: d.item.displayName, text: d.item.factText };
      }
    } else if (event === "session:complete") {
      const result = data as {
        correctCount: number;
        wrongCount: number;
        finalScore: number;
        bestStreak: number;
        itemsMatched: string[];
      };
      onComplete(result);
    }
  };

  onMount(() => {
    const round = RoundGenerator.generate(items, categories, { itemsPerRound: Math.min(10, items.length) });
    session = new MatchSession([round]);
    session.on(listener);
    session.start();
  });

  onDestroy(() => {
    session?.off(listener);
  });

  function pickCategory(catId: string): void {
    session?.answer(catId);
  }
</script>

<BaseScene sceneId="match-game" ariaLabel="Ocean Match gameplay" onEscape={onPause}>
  <div class="game">
    <header class="game__hud" aria-live="polite">
      <span class="game__score">Score: {score}</span>
      <span class="game__streak">Streak: {streak}</span>
      <button type="button" class="game__pause" on:click={onPause} aria-label="Pause">II</button>
    </header>

    {#if currentItem}
      <div class="game__item">
        <img src={currentItem.spritePath} alt={currentItem.displayName} />
        <h2>{currentItem.displayName}</h2>
        <p>Which habitat does it live in?</p>
      </div>

      <div class="game__choices">
        {#each currentCats as cat (cat.id)}
          <button
            type="button"
            class="game__choice"
            on:click={() => pickCategory(cat.id)}
            disabled={feedback !== ""}
          >
            {cat.displayName}
          </button>
        {/each}
      </div>

      {#if feedback === "correct"}
        <p class="game__feedback game__feedback--correct" role="status">Yes! Great match.</p>
      {:else if feedback === "wrong"}
        <p class="game__feedback game__feedback--wrong" role="status">Not quite. Try again next time.</p>
      {/if}

      {#if factCard}
        <div class="game__fact" role="dialog" aria-modal="true" aria-label="Fact about {factCard.name}">
          <h3>{factCard.name}</h3>
          <p>{factCard.text}</p>
        </div>
      {/if}
    {/if}
  </div>
</BaseScene>

<style>
  .game {
    max-width: 720px;
    margin: 0 auto;
    padding: 1rem;
  }
  .game__hud {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0.5rem 1rem;
    background: var(--color-surface);
    border-radius: 0.5em;
    margin-bottom: 1rem;
    font-weight: 600;
  }
  .game__pause {
    min-width: 44px;
    min-height: 44px;
    background: transparent;
    border: 1px solid var(--color-divider);
    border-radius: 0.25em;
    cursor: pointer;
    font-weight: bold;
  }
  .game__item {
    text-align: center;
    margin-bottom: 2rem;
  }
  .game__item img {
    max-width: 300px;
    width: 60%;
    height: auto;
  }
  .game__item h2 {
    color: var(--color-heading);
    margin: 0.5rem 0;
  }
  .game__choices {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
    gap: 0.75rem;
    margin-bottom: 1rem;
  }
  .game__choice {
    min-height: 44px;
    padding: 0.75em;
    background: var(--color-accent);
    color: var(--color-on-accent);
    border: none;
    border-radius: 0.5em;
    font-size: 1rem;
    font-weight: 600;
    cursor: pointer;
  }
  .game__choice:focus-visible {
    outline: 3px solid var(--focus-ring);
    outline-offset: 2px;
  }
  .game__choice:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
  .game__feedback {
    text-align: center;
    font-size: 1.25rem;
    font-weight: 600;
    padding: 0.5rem;
    border-radius: 0.25em;
  }
  .game__feedback--correct {
    color: #1b5e20;
    background: #c8e6c9;
  }
  .game__feedback--wrong {
    color: #b71c1c;
    background: #ffcdd2;
  }
  .game__fact {
    margin-top: 1rem;
    padding: 1rem;
    background: var(--color-surface);
    border-left: 4px solid var(--color-accent);
    border-radius: 0.25em;
  }
  .game__fact h3 {
    color: var(--color-heading);
    margin: 0 0 0.5rem 0;
  }
  .game__fact p {
    margin: 0;
  }
</style>
